
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type Language = {
  name: string;
  code: string;
  flag: string;
};

const languages: Language[] = [
  { name: "English", code: "en", flag: "/images/flags/usa.svg" },
  { name: "Türkçe", code: "tr", flag: "/images/flags/turkey.svg" },
  { name: "Azərbaycan", code: "az", flag: "/images/flags/azerbaijan.svg" },
];

const ChooseLanguage: React.FC = () => {
  const { i18n, t } = useTranslation();

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.setAttribute("lang", code);
    document.documentElement.setAttribute("dir", code === "ar" ? "rtl" : "ltr");
  };

  const [active, setActive] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

  const handleDropdownToggle = () => {
    setActive((prevState) => !prevState);
  };

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActive(false); // Close the dropdown if clicked outside
      }
    };

    // Attach the event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className="relative language-menu mx-[8px] md:mx-[10px] lg:mx-[12px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={handleDropdownToggle}
        className={`leading-none pr-[12px] inline-block transition-all relative top-[2px] hover:text-primary-500 ${
          active ? "active" : ""
        }`}
      >
        <i className="material-symbols-outlined !text-[20px] md:!text-[22px]">
          translate
        </i>
        <i className="ri-arrow-down-s-line text-[15px] absolute -right-[3px] top-1/2 -translate-y-1/2 -mt-[2px]"></i>
      </button>

      {active && (
        <div className="language-menu-dropdown bg-white dark:bg-[#0c1427] transition-all shadow-3xl dark:shadow-none pt-[13px] md:pt-[14px] absolute mt-[18px] md:mt-[21px] w-[200px] md:w-[240px] z-[1] top-full ltr:left-0 ltr:md:left-auto ltr:lg:right-0 rtl:right-0 rtl:md:right-auto rtl:lg:left-0 rounded-md">
          <span className="block text-black dark:text-white font-semibold px-[20px] pb-[14px] text-sm md:text-[15px]">
            {t("common.chooseLanguage")}
          </span>

          <ul>
            {languages.map((language) => (
              <li
                key={language.code}
                className={`border-t border-dashed border-gray-100 dark:border-[#172036] ${
                  i18n.language === language.code ? "bg-primary-50 dark:bg-primary-900" : ""
                }`}
              >
                <button
                  type="button"
                  className="text-black dark:text-white px-[20px] py-[12px] d-block w-full font-medium hover:text-primary-500"
                  onClick={() => {
                    handleLanguageChange(language.code);
                    setActive(false);
                  }}
                >
                  <div className="flex items-center">
                    <img
                      src={language.flag}
                      className="ltr:mr-[10px] rtl:ml-[10px]"
                      alt={language.name}
                      width={30}
                      height={30}
                    />
                    {language.name}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ChooseLanguage;
